import type { SxProps, Theme } from "@mui/material/styles";

/**
 * SI Console table look, applied to a Box wrapping the shared library
 * `DataGrid` (which doesn't take an `sx` of its own). Mirrors the SI Console's
 * `StripedDataGrid` / `CommonListView`:
 *  - grey surface behind the grid (#d8d8d8 light / #1c1c1c dark),
 *  - header on that same surface,
 *  - striped rows: even white/charcoal, odd grey,
 *  - grey hover.
 *
 * The library tags rows with `act-row-even` / `act-row-odd`; we override those
 * with the SI colors (!important beats the library's own row sx).
 */
export function siConsoleGridSx(mode: "light" | "dark"): SxProps<Theme> {
  const surface = mode === "dark" ? "#1c1c1c" : "#d8d8d8";
  const evenRow = mode === "dark" ? "#1c1c1c" : "#ffffff";
  const oddRow = mode === "dark" ? "#1c1c1c" : "#d8d8d8";
  const hover = "rgba(189, 189, 189, 0.2)"; // alpha(grey[400], 0.2), as in SI

  return {
    "& .MuiDataGrid-root": {
      backgroundColor: surface,
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: `${surface} !important`,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
    },
    "& .MuiDataGrid-row.act-row-even": {
      backgroundColor: `${evenRow} !important`,
    },
    "& .MuiDataGrid-row.act-row-odd": {
      backgroundColor: `${oddRow} !important`,
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: `${hover} !important`,
    },
    "& .MuiDataGrid-cell": {
      fontSize: "0.875rem",
    },
  };
}

/**
 * SI Console table look for a plain MUI `<Table>` (e.g. the verification
 * documents table), matching {@link siConsoleGridSx}: grey header surface with
 * bold titles, striped rows (even white/charcoal, odd grey) and grey hover.
 */
export function siConsoleTableSx(mode: "light" | "dark"): SxProps<Theme> {
  const surface = mode === "dark" ? "#1c1c1c" : "#d8d8d8";
  const evenRow = mode === "dark" ? "#1c1c1c" : "#ffffff";
  const oddRow = mode === "dark" ? "#1c1c1c" : "#d8d8d8";
  const hover = "rgba(189, 189, 189, 0.2)"; // alpha(grey[400], 0.2), as in SI

  return {
    "& .MuiTableCell-root": {
      fontSize: "0.875rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      backgroundColor: surface,
      fontWeight: 700,
    },
    // nth-of-type(odd) = 1st, 3rd rows → the "even" (0-based) white stripe.
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
      backgroundColor: evenRow,
    },
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
      backgroundColor: oddRow,
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: hover,
    },
  };
}
